<template>
  <v-row>
    <v-col :md="overViewPage ? 6 : 12">
      <v-row>
        <!-- Loading: two field-sized skeleton bars mirroring the region +
             facility layout, so the placeholder matches the loaded state
             instead of collapsing to one full-width block (regions aren't known
             yet while loading). Kept separate from the fields so the skeleton's
             overflow clipping never crops the outlined labels. -->
        <template v-if="organizationsLoading">
          <v-col cols="12" md="6">
            <v-skeleton-loader
              type="image"
              :height="40"
              class="rounded overflow-hidden"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-skeleton-loader
              type="image"
              :height="40"
              class="rounded overflow-hidden"
            />
          </v-col>
        </template>

        <template v-else>
          <!-- Only show the region selector when the user actually has regions
               in scope. Facility-role managers have none (their accessible scope
               is just facilities), so the facility multiselect stands alone. -->
          <v-col v-if="hasRegions" cols="12" md="6">
            <v-menu
              :close-on-content-click="false"
              offset-y
              max-height="450"
              content-class="filter-menu"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  :value="regionSummary"
                  label="Select Organization/Region"
                  placeholder="Select Organization/Region"
                  readonly
                  outlined
                  dense
                  hide-details="auto"
                  append-icon="mdi-menu-down"
                  v-bind="attrs"
                  v-on="on"
                  @click:append="on.click"
                />
              </template>
              <v-card>
                <v-list-item ripple @click="allSelected = !allSelected">
                  <v-list-item-action>
                    <v-icon :color="selectedRegions.length ? 'primary' : ''">
                      {{ regionSelectAllIcon }}
                    </v-icon>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>Select All</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-divider />
                <v-treeview
                  v-model="selectedRegions"
                  :items="organizationTree"
                  item-key="id"
                  item-text="name"
                  selectable
                  selected-color="primary"
                  return-object
                  selection-type="leaf"
                  open-all
                  dense
                >
                  <template #label="{ item }">
                    <div
                      class="d-flex align-center scope-row"
                      :class="{
                        'scope-row--selected': isRegionSelected(item),
                      }"
                    >
                      <span
                        class="text-truncate"
                        :class="{
                          'primary--text font-weight-medium':
                            isRegionSelected(item),
                        }"
                      >
                        {{ item.name }}
                      </span>
                    </div>
                  </template>
                </v-treeview>
              </v-card>
            </v-menu>
          </v-col>
          <v-col cols="12" :md="hasRegions ? 6 : 12">
            <v-autocomplete
              v-if="facilitySingleSelect"
              v-model="selectedFacility"
              :items="facilities"
              :filter="facilityFilter"
              label="Select Facility"
              dense
              outlined
              item-text="name"
              :return-object="true"
            >
              <template #item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                  <v-list-item-subtitle v-if="item.locationCode">
                    {{ item.locationCode }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
            <v-autocomplete
              v-else
              v-model="selectedFacilities"
              :items="facilities"
              :filter="facilityFilter"
              label="Select Facilities"
              class="facility-select"
              :menu-props="{ contentClass: 'filter-menu' }"
              dense
              multiple
              outlined
              clearable
              hide-details="auto"
              item-text="name"
              :return-object="true"
              clear-icon="far fa-times-circle"
            >
              <template #prepend-item>
                <v-list-item
                  ripple
                  @mousedown.prevent
                  @click="selectAllFacilities = !selectAllFacilities"
                >
                  <v-list-item-action>
                    <v-icon :color="selectedFacilities.length ? 'primary' : ''">
                      {{ facilitySelectAllIcon }}
                    </v-icon>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>Select All</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-divider />
              </template>
              <!-- Supplying #item replaces Vuetify's own checkbox rendering
                   (genAction), so the checkbox is rendered here instead. Raw slot
                   content (not a v-list-item) is still wrapped in a clickable tile
                   by Vuetify, so row clicks keep working; the checkbox itself stops
                   propagation, hence @input="on.click" — matching what genAction
                   does — so a click on either lands exactly one toggle. -->
              <template #item="{ item, attrs, on }">
                <v-list-item-action>
                  <v-simple-checkbox
                    :value="attrs.inputValue"
                    color="primary"
                    :ripple="false"
                    @input="on.click"
                  />
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                  <v-list-item-subtitle v-if="item.locationCode">
                    {{ item.locationCode }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
              <!-- Single-line summary (only rendered once, for index 0) so the
                   field stays one line tall regardless of how many are picked.
                   min-width:0 lets text-truncate actually clip inside the flex
                   selections row instead of pushing to a new line. -->
              <template #selection="{ index }">
                <span
                  v-if="index === 0"
                  class="text-truncate"
                  style="min-width: 0"
                >
                  {{ facilitySummary }}
                </span>
              </template>
            </v-autocomplete>
          </v-col>
        </template>
      </v-row>
    </v-col>
  </v-row>
</template>
<script>
import { computed } from "vue";
import useUser from "@components/authentication/useUser";
import useAccessibleScope from "@components/authentication/useAccessibleScope";
import { matchesFacility } from "@components/common/helpers/facility";
import useDashboard from "./useDashboard";
export default {
  props: {
    overViewPage: {
      type: Boolean,
      default: false,
    },
    facilitySingleSelect: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const { defaultOrganization } = useUser();
    const { hasSystemScope, topLevelOrganization } = useAccessibleScope();
    const {
      fetchOrganizations,
      fetchAccessibleOrganizations,
      regions,
      facilities,
      selectedRegions,
      selectedFacilities,
      organizationsLoading,
      organizationTree,
      selectedFacility,
    } = useDashboard();
    // v-treeview selection-type="leaf" tracks only leaf nodes (regions with no
    // child regions), not their parent orgs — so "Select All" must reflect and
    // set the leaf set, otherwise it never reads as fully checked.
    const collectLeaves = (nodes, acc = []) => {
      (nodes || []).forEach((node) => {
        if (node.children && node.children.length) {
          collectLeaves(node.children, acc);
        } else {
          acc.push(node);
        }
      });
      return acc;
    };
    const leafRegions = computed(() => collectLeaves(organizationTree.value));

    // Selected-node highlight helper for the region tree label slot.
    const selectedRegionIds = computed(
      () => new Set(selectedRegions.value.map((r) => r.id))
    );
    // A node reads as "selected" (highlighted label + tinted row) when every
    // leaf under it is selected — so a leaf lights up on its own, and a parent
    // lights up only when its whole subtree is chosen. Matches the checkbox.
    const isRegionSelected = (item) => {
      const leaves = collectLeaves([item]);
      return (
        leaves.length > 0 &&
        leaves.every((leaf) => selectedRegionIds.value.has(leaf.id))
      );
    };

    // Derived "Select All" state — computed (not a ref + watch) so it reflects
    // the real selection immediately on page load (regions are selected by
    // useDashboard as data arrives) and stays in sync when the user toggles the
    // tree. The setter selects all leaves / clears.
    const allSelected = computed({
      get() {
        return (
          leafRegions.value.length > 0 &&
          selectedRegions.value.length >= leafRegions.value.length
        );
      },
      set(value) {
        selectedRegions.value = value ? [...leafRegions.value] : [];
      },
    });

    // Hide the region selector entirely when there are no regions in scope
    // (facility-role managers) — an empty region dropdown reads as broken.
    const hasRegions = computed(() => regions.value.length > 0);

    // Single-line summary for a multiselect: always the first item's name plus
    // "(+N others)" for the rest — including when everything is selected — so the
    // field stays one line tall and reads consistently.
    function summarize(selected) {
      if (!selected.length) return "";
      return selected.length > 1
        ? `${selected[0].name} (+${selected.length - 1} others)`
        : selected[0].name;
    }

    // Breadcrumb path for a node, walking parentId up through the region/org
    // list — e.g. "Monument - StorageDefender / Monument - Region" — so the
    // field shows where the selected region sits in the tree, not just its name.
    function regionPath(node) {
      const byId = new Map(regions.value.map((o) => [o.id, o]));
      const names = [];
      const seen = new Set();
      let current = node;
      while (current && !seen.has(current.id)) {
        seen.add(current.id);
        names.unshift(current.name);
        current = current.parentId ? byId.get(current.parentId) : null;
      }
      return names.join(" > ");
    }

    // In leaf mode selectedRegions holds the selected leaf nodes — regions, or
    // the organization itself when it has no child regions — so summarize the
    // selection directly (filtering to type "region" would blank the field for
    // an org-only scope).
    const regionSummary = computed(() => {
      const selected = selectedRegions.value;
      if (!selected.length) return "";
      const firstPath = regionPath(selected[0]);
      return selected.length > 1
        ? `${firstPath} (+${selected.length - 1} others)`
        : firstPath;
    });
    const facilitySummary = computed(() => summarize(selectedFacilities.value));

    const selectAllFacilities = computed({
      get() {
        return (
          facilities.value.length > 0 &&
          selectedFacilities.value.length >= facilities.value.length
        );
      },
      set(value) {
        selectedFacilities.value = value ? [...facilities.value] : [];
      },
    });

    // Match typed text against facility name OR SiteLink Location Code (SS-105).
    // Set explicitly rather than relying on Vuetify's default filter, which only
    // ever compares the displayed item-text ("name").
    const facilityFilter = (item, queryText) =>
      matchesFacility(item, queryText);

    // Shared tri-state "Select All" icon (all / some / none) so the region and
    // facility select-all rows render identically.
    const selectAllIcon = (all, selectedCount) => {
      if (all) return "mdi-checkbox-marked";
      if (selectedCount) return "mdi-minus-box";
      return "mdi-checkbox-blank-outline";
    };
    const regionSelectAllIcon = computed(() =>
      selectAllIcon(allSelected.value, selectedRegions.value.length)
    );
    const facilitySelectAllIcon = computed(() =>
      selectAllIcon(selectAllFacilities.value, selectedFacilities.value.length)
    );

    return {
      defaultOrganization,
      hasSystemScope,
      topLevelOrganization,
      fetchOrganizations,
      fetchAccessibleOrganizations,
      regions,
      hasRegions,
      facilities,
      selectedRegions,
      selectedFacilities,
      organizationsLoading,
      organizationTree,
      allSelected,
      regionSelectAllIcon,
      isRegionSelected,
      regionSummary,
      facilitySummary,
      selectAllFacilities,
      facilitySelectAllIcon,
      selectedFacility,
      facilityFilter,
    };
  },
  beforeMount() {
    const types = ["region", "facility", "organization"];

    // A platform/application admin (including one who also holds lower
    // region/facility roles, so their default org is a facility) sees the whole
    // tree under their top-level org via the single-root descendants fetch. The
    // bounded union would show only the facilities their lower roles cover.
    if (this.hasSystemScope) {
      this.fetchOrganizations(
        this.$apollo,
        this.topLevelOrganization.id,
        types
      );
      return;
    }

    // Source the selectors from the union of the user's accessible scope (every
    // subtree where they hold a role) for all bounded tiers — facility, region,
    // AND organization managers. A facility manager whose facilities span more
    // than one organization must see them all here, matching the header switcher;
    // the single-root descendants fetch showed only the default org's subtree.
    // Only the broadest admins (platform/application) — whose default-org subtree
    // already IS their whole scope — keep the single-root fetch.
    const useAccessibleUnion = types.includes(this.defaultOrganization?.type);
    if (useAccessibleUnion) {
      this.fetchAccessibleOrganizations(this.$apollo, types);
    } else {
      this.fetchOrganizations(this.$apollo, this.defaultOrganization.id, types);
    }
  },
};
</script>

<style scoped>
/* Keep the facility summary on a single line: stop the autocomplete's selection
   row from wrapping so the truncated "All selected" / "Name (+N others)" text
   never spills onto a second line and grows the field. */
.facility-select ::v-deep .v-select__selections {
  flex-wrap: nowrap;
}
</style>

<!-- Both dropdowns render as teleported overlays (outside this component's
     scoped DOM), so their shared styling is targeted globally via the
     `filter-menu` content-class set on the region v-menu and the facility
     autocomplete's menu-props. -->
<style>
/* Uniform typography: same size/weight for item labels + Select All in the
   region tree and the facility list. */
.filter-menu .v-list-item__title,
.filter-menu .v-treeview-node__label {
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.4;
}
/* Location code line under a facility name — smaller and quieter than the name so
   the name stays the primary label while the code explains a code-search match. */
.filter-menu .v-list-item__subtitle {
  font-size: 0.75rem;
  line-height: 1.3;
}
/* Selected item emphasis (medium weight) — matches the region label's
   font-weight-medium utility so both read identically. */
.filter-menu .v-list-item--active .v-list-item__title {
  font-weight: 500;
}
.filter-menu .scope-row {
  width: 100%;
  min-width: 0;
}
/* Tint the WHOLE selected tree row (checkbox included), not just the label, so
   it matches the facility list's full-row active highlight. `:has` scopes the
   tint to a node whose own label is selected (children sit outside the root). */
.filter-menu .v-treeview-node__root:has(.scope-row--selected) {
  background: rgba(var(--v-primary-base-rgb, 45, 46, 117), 0.08);
}
</style>
